import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Assuming you are using react-router-dom
import Sidedash from '../uicomponents/Sidedash';
import { Formik, Form as FormikForm, FieldArray, useField } from 'formik';
import * as Yup from 'yup';
import { Container, Row, Col, Button, Form as BForm, Card, Spinner, Alert } from 'react-bootstrap';

// Custom input components for clean Formik+Bootstrap integration
const TextInput = ({ label, ...props }) => {
  const [field, meta] = useField(props.name);
  return (
    <BForm.Group controlId={props.name} className="mb-3">
      <BForm.Label>{label}</BForm.Label>
      <BForm.Control
        {...field}
        {...props}
        isInvalid={meta.touched && !!meta.error}
      />
      <BForm.Control.Feedback type="invalid">
        {meta.error}
      </BForm.Control.Feedback>
    </BForm.Group>
  );
};

const CheckboxInput = ({ label, ...props }) => {
  const [field, meta] = useField({ ...props, type: 'checkbox' });
  return (
    <BForm.Group controlId={props.name} className="mb-3">
      <BForm.Check
        {...field}
        {...props}
        label={label}
        isInvalid={meta.touched && !!meta.error}
      />
      <BForm.Control.Feedback type="invalid">
        {meta.error}
      </BForm.Control.Feedback>
    </BForm.Group>
  );
};

const BookingEdit = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const host = process.env.REACT_APP_BACKEND_URL;

  const fetchBooking = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetch(`${host}/bookings/api/singlebookings/${id}`);
      if (!response.ok) throw new Error('Failed to load booking');
      const data = await response.json();
      setBooking(data);
    } catch (error) {
      console.error('Error fetching booking:', error);
      setLoadError('Unable to load booking data');
    } finally {
      setLoading(false);
    }
  }, [host, id]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitError('');
    try {
      const response = await fetch(`${host}/bookings/api/edit/bookings/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': localStorage.getItem('token'),
        },
        body: JSON.stringify(values),
      });
      if (response.ok) {
        navigate('/bookings');
      } else {
        const errorData = await response.json();
        setSubmitError(errorData.message || 'Error updating booking');
      }
    } catch (error) {
      setSubmitError('Network error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object().shape({
    bookedBy: Yup.string().required('Booked By is required'),
    bookingDate: Yup.date().required('Booking Date is required'),
    amount: Yup.number().min(0, 'Amount must be positive').required('Amount is required'),
    discountCoupon: Yup.string(),
    discountValue: Yup.number().min(0, 'Discount Value must be positive'),
    totalPrice: Yup.number().min(0, 'Total Price must be positive').required('Total Price is required'),
    paymentMode: Yup.string().required('Payment Mode is required'),
    orderStatus: Yup.boolean().required('Order Status is required'),
    pCash: Yup.number().min(0, 'Payment Cash must be positive').required('Payment Cash is required'),
    pOnline: Yup.number().min(0, 'Payment Online must be positive').required('Payment Online is required'),
    pInfo: Yup.string(),
    customerName: Yup.string().required('Customer Name is required'),
    customerEmail: Yup.string().email('Invalid email').required('Customer Email is required'),
    customerMobile: Yup.string().matches(/^[0-9]{10}$/, 'Mobile must be 10 digits').required('Customer Mobile is required'),
    udf1: Yup.number().min(0, 'Amount Paid must be positive'),
    udf2: Yup.number().min(0, 'Payment Due must be positive'),
    udf3: Yup.string(),
    paymentStatus: Yup.string().required('Payment Status is required'),
    seatDetails: Yup.array().of(
      Yup.object().shape({
        seatNumber: Yup.string().required('Seat Number is required'),
        slot: Yup.string().required('Slot is required'),
        seatValidTill: Yup.date().required('Valid Till is required'),
        type: Yup.string().required('Type is required'),
      })
    ).min(1, 'At least one seat is required'),
  });

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <Container fluid className="p-0">
        <Row>
          <Col className="text-center mt-5">
            <Spinner animation="border" />
          </Col>
        </Row>
      </Container>
    );
  }
  if (loadError) {
    return (
      <Container fluid>
        <Row>
          <Col className="p-4">
            <Alert variant="danger">{loadError}</Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">

        <Sidedash />

        <Col md={10} className="p-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Edit Booking</h5>
            </Card.Header>
            <Card.Body>
              <Formik
                initialValues={{
                  ...booking,
                  bookingDate: booking.bookingDate ? formatDateForInput(booking.bookingDate) : '',
                  seatDetails: booking.seatDetails.map(seat => ({
                    ...seat,
                    seatValidTill: seat.seatValidTill ? formatDateForInput(seat.seatValidTill) : '',
                  })),
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, values, errors, touched }) => (
                  <FormikForm className="mb-4">
                    {submitError && <Alert variant="danger">{submitError}</Alert>}
                    <Row className="mb-4">
                      <Col md={6}>
                        <TextInput label="Booked By" name="bookedBy" type="text" />
                      </Col>
                      <Col md={6}>
                        <TextInput label="Booking Date" name="bookingDate" type="date" />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={6}>
                        <TextInput label="Client Txn ID" name="clientTxnId" type="text" disabled />
                      </Col>
                      <Col md={6}>
                        <TextInput label="Amount" name="amount" type="number" />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={6}>
                        <TextInput label="Discount Coupon" name="discountCoupon" type="text" />
                      </Col>
                      <Col md={6}>
                        <TextInput label="Discount Value" name="discountValue" type="number" />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={6}>
                        <TextInput label="Total Price" name="totalPrice" type="number" />
                      </Col>
                      <Col md={6}>
                        <TextInput label="Payment Mode" name="paymentMode" type="text" />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={6}>
                        <CheckboxInput label="Order Status" name="orderStatus" />
                      </Col>
                      <Col md={6}>
                        <TextInput label="Payment Cash" name="pCash" type="number" />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={6}>
                        <TextInput label="Payment Online" name="pOnline" type="number" />
                      </Col>
                      <Col md={6}>
                        <TextInput label="Payment Info" name="pInfo" type="text" />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={6}>
                        <TextInput label="Customer Name" name="customerName" type="text" />
                      </Col>
                      <Col md={6}>
                        <TextInput label="Customer Email" name="customerEmail" type="email" />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={6}>
                        <TextInput label="Customer Mobile" name="customerMobile" type="text" />
                      </Col>
                      <Col md={6}>
                        <TextInput label="Amount Paid" name="udf1" type="number" />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={6}>
                        <TextInput label="Payment Due" name="udf2" type="number" />
                      </Col>
                      <Col md={6}>
                        <TextInput label="Notes" name="udf3" type="text" />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={6}>
                        <TextInput label="Payment Status" name="paymentStatus" type="text" />
                      </Col>
                    </Row>
                    <FieldArray name="seatDetails">
                      {({ remove, push }) => (
                        <>
                          <h5 className="mt-4">Seat Details</h5>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => push({ seatNumber: '', slot: '', seatValidTill: '', type: '' })}
                            className="mb-3"
                          >
                            Add Seat
                          </Button>
                          {values.seatDetails.map((_, index) => (
                            <Card key={index} className="mb-3">
                              <Card.Header className="d-flex justify-content-between align-items-center">
                                <span>Seat {index + 1}</span>
                                <Button variant="outline-danger" size="sm" onClick={() => remove(index)}>
                                  Remove
                                </Button>
                              </Card.Header>
                              <Card.Body>
                                <Row className="mb-3">
                                  <Col md={6}>
                                    <TextInput
                                      label="Seat Number"
                                      name={`seatDetails.${index}.seatNumber`}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <TextInput label="Slot" name={`seatDetails.${index}.slot`} />
                                  </Col>
                                </Row>
                                <Row className="mb-3">
                                  <Col md={6}>
                                    <TextInput
                                      label="Valid Till"
                                      name={`seatDetails.${index}.seatValidTill`}
                                      type="date"
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <TextInput label="Type" name={`seatDetails.${index}.type`} />
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          ))}
                        </>
                      )}
                    </FieldArray>
                    <div className="text-right mt-3">
                      <Button type="submit" variant="primary" disabled={isSubmitting}>
                        {isSubmitting ? 'Updating...' : 'Update Booking'}
                      </Button>
                    </div>
                  </FormikForm>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </div>
    </div>
  );
};

export default BookingEdit;
